import * as React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import moment from 'moment';
import propz from 'propz';
import { SimpleModal } from 'Src/components/SimpleModal/SimpleModal';
import { getUnionDashboard } from 'Src/helpers/service/adminSU/dashboard';
import { Tournament } from 'Src/models/tournament';
import { AppUser } from 'Src/views/App/App';
import './UnionDashboard.css';

interface Props {
  user: AppUser;
}

interface DashboardData {
  summary: {
    upcomingCount: number;
    archiveCount: number;
    invitesCount: number;
    unansweredConfirmationCount: number;
    schoolsCount: number;
  };
  tournaments: DashboardTournament[];
  attentionItems: AttentionItem[];
  calendarDays: Array<{ date: string; count: number; tournamentIds: string[] }>;
}

interface DashboardTournament extends Tournament {
  participantsCount?: number;
  teamsCount?: number;
  unansweredConfirmationRequestsCount?: number;
  confirmationDeadline?: string;
  structureReviewStatus?: string;
  progress?: { totalEvents: number; finishedEvents: number; percent: number };
}

type AttentionType = 'CONFIRMATIONS' | 'STRUCTURE' | 'WAITING_LIST' | 'PUBLIC_DISPLAY';
interface AttentionItem {
  type: AttentionType;
  tournamentId: string;
  tournamentName: string;
  startTime: string;
  count?: number;
  deadline?: string;
  status?: string;
  publicSiteEnabled?: boolean;
  bigscreenEnabled?: boolean;
}

const number = value => Number(value || 0);

const getCapacity = (tournament: Tournament) =>
  (tournament.autoEnrollmentTeamsSettings || []).reduce(
    (result, setting) => ({
      booked: result.booked + number(setting.currentNumberTeams),
      capacity: result.capacity + number(setting.maximumNumberTeams)
    }),
    { booked: 0, capacity: 0 }
  );

const getVenue = (tournament: Tournament) =>
  propz.get(tournament, ['venue', 'placeData', 'name']) ||
  propz.get(tournament, ['venue', 'postcode']) ||
  'Venue not set';

const getPicture = (tournament: Tournament) => {
  const pictures: any[] = tournament.photos || [];
  const hero = pictures.find(picture => picture.isHero || picture.isMain) || pictures[0];
  return hero && (hero.picUrl || hero.pic || hero.url || hero.imageUrl);
};

const ATTENTION_META: Record<AttentionType, { title: string; subtitle: string; icon: string }> = {
  CONFIRMATIONS: { title: 'Unanswered confirmation requests', subtitle: 'Schools still need to reply', icon: '!' },
  STRUCTURE: { title: 'Structure reviews', subtitle: 'Knockout progression needs checking', icon: '↗' },
  WAITING_LIST: { title: 'Waiting lists', subtitle: 'Schools are waiting for places', icon: 'W' },
  PUBLIC_DISPLAY: { title: 'Public displays to finish', subtitle: 'Website or TV is switched off', icon: 'TV' }
};

export const UnionDashboard = ({ user }: Props) => {
  const history = useHistory();
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [activeAttention, setActiveAttention] = useState<AttentionType>('CONFIRMATIONS');
  const [calendarMonth, setCalendarMonth] = useState(() => moment().startOf('month'));

  const load = useCallback(() => {
    setLoading(true);
    setError(false);
    getUnionDashboard(user)
      .then(result => setData(result))
      .catch(() => setError(true))
      .then(() => setLoading(false));
  }, [user]);

  useEffect(load, [load]);

  const attentionCounts = useMemo(() => {
    const result = { CONFIRMATIONS: 0, STRUCTURE: 0, WAITING_LIST: 0, PUBLIC_DISPLAY: 0 };
    (data && data.attentionItems || []).forEach(item => { result[item.type] += 1; });
    if (data) result.CONFIRMATIONS = number(data.summary.unansweredConfirmationCount);
    return result;
  }, [data]);

  const visibleAttentionItems = useMemo(
    () => (data && data.attentionItems || []).filter(item => item.type === activeAttention).slice(0, 5),
    [data, activeAttention]
  );

  const calendarCountByDay = useMemo(() => {
    const result = new Map<string, number>();
    (data && data.calendarDays || []).forEach(day => result.set(day.date, day.count));
    return result;
  }, [data]);

  const calendarCells = useMemo(() => {
    const first = calendarMonth.clone().startOf('month');
    const leading = (first.day() + 6) % 7;
    const cells: Array<moment.Moment | null> = Array(leading).fill(null);
    for (let day = 1; day <= calendarMonth.daysInMonth(); day += 1) cells.push(calendarMonth.clone().date(day));
    while (cells.length % 7) cells.push(null);
    return cells;
  }, [calendarMonth]);

  const openTournament = tournamentId =>
    history.push({
      pathname: '/tournaments/manage',
      search: `tournament=${tournamentId}`,
      state: { prevPath: '/dashboard' }
    });

  const openTournamentSection = (event: React.MouseEvent, tournamentId: string, path: string) => {
    event.stopPropagation();
    history.push({ pathname: path, search: `tournament=${tournamentId}`, state: { prevPath: '/dashboard' } });
  };

  const openCalendarDay = day => {
    const formatted = day.format('DD-MM-YYYY');
    history.push(`/tournaments/upcoming?startTime=FROM_${formatted}&startTime=TO_${formatted}`);
  };

  const attentionAction = (item: AttentionItem) => {
    if (item.type === 'CONFIRMATIONS') return `/tournaments/confirmationRequests?tournament=${item.tournamentId}`;
    if (item.type === 'WAITING_LIST') return `/tournaments/waitingList?tournament=${item.tournamentId}`;
    if (item.type === 'STRUCTURE') return `/tournaments/manage/competition/structure?tournament=${item.tournamentId}`;
    return `/tournaments/manage/settings?tournament=${item.tournamentId}`;
  };

  const unionName = user.activeSchoolName || propz.get(user, ['activeSchool', 'name']) || 'Your union';
  const summary = data && data.summary;

  return (
    <main className="unionDashboard">
      <section className="unionDashboardHero">
        <div>
          <span className="unionDashboardEyebrow">Union overview</span>
          <h1>{unionName}</h1>
          <p>Everything that needs your attention, followed by the tournaments coming up next.</p>
        </div>
        <div className="unionDashboardHeroActions">
          <Link to="/tournaments/upcoming">View tournaments</Link>
          <Link className="isPrimary" to={{ pathname: '/tournaments/tournamentView', state: { prevPath: '/dashboard' } }}>+ Add tournament</Link>
        </div>
      </section>

      {loading ? (
        <div className="unionDashboardLoading" aria-live="polite">Loading your dashboard…</div>
      ) : data && summary ? (
        <>
          <section className="unionDashboardStats" aria-label="Union summary">
            <Link to="/tournaments/upcoming"><b>{number(summary.upcomingCount)}</b><span>Upcoming tournaments</span></Link>
            <Link to="/schools"><b>{number(summary.schoolsCount)}</b><span>Member schools</span></Link>
            <Link to="/tournaments/invites/outbox"><b>{number(summary.invitesCount)}</b><span>Open invitations</span></Link>
            <Link to="/tournaments/archive"><b>{number(summary.archiveCount)}</b><span>Past tournaments</span></Link>
          </section>

          <section className="unionDashboardSection">
            <div className="unionDashboardSectionHeading">
              <div><span>Action centre</span><h2>Attention required</h2></div>
              <p>Resolve the most important tournament tasks first.</p>
            </div>
            <div className="unionDashboardAttention">
              {(Object.keys(ATTENTION_META) as AttentionType[]).map(type => {
                const meta = ATTENTION_META[type];
                return (
                  <button
                    type="button"
                    key={type}
                    className={`${activeAttention === type ? 'isActive' : ''}${type === 'CONFIRMATIONS' && attentionCounts[type] ? ' isUrgent' : ''}`}
                    onClick={() => setActiveAttention(type)}
                  >
                    <span className="unionDashboardAttentionIcon">{meta.icon}</span>
                    <div><b>{attentionCounts[type]}</b><strong>{meta.title}</strong><small>{meta.subtitle}</small></div>
                    <i>{activeAttention === type ? 'Selected' : 'View'} →</i>
                  </button>
                );
              })}
            </div>
            <div className="unionDashboardTaskList">
              <div className="unionDashboardTaskListHead">
                <strong>{ATTENTION_META[activeAttention].title}</strong>
                {activeAttention === 'CONFIRMATIONS' ? <Link to="/tournaments/upcoming?confirmationReplies=unanswered">View all →</Link> : null}
              </div>
              {visibleAttentionItems.length ? visibleAttentionItems.map(item => (
                <Link key={`${item.type}-${item.tournamentId}`} to={attentionAction(item)}>
                  <span><b>{item.tournamentName}</b><small>{moment(item.startTime).format('ddd, DD MMM YYYY')}</small></span>
                  <em>
                    {item.type === 'CONFIRMATIONS' ? `${number(item.count)} awaiting reply` : null}
                    {item.type === 'WAITING_LIST' ? `${number(item.count)} waiting` : null}
                    {item.type === 'STRUCTURE' ? (item.status === 'REQUIRES_DECISION' ? 'Conflict to resolve' : 'Review progression') : null}
                    {item.type === 'PUBLIC_DISPLAY' ? `${item.publicSiteEnabled ? '' : 'Website'}${!item.publicSiteEnabled && !item.bigscreenEnabled ? ' & ' : ''}${item.bigscreenEnabled ? '' : 'TV'} off` : null}
                  </em>
                  {item.deadline ? <small className={moment(item.deadline).isBefore(moment()) ? 'isOverdue' : ''}>Reply by {moment(item.deadline).format('DD MMM, HH:mm')}</small> : null}
                  <i>Open →</i>
                </Link>
              )) : <div className="unionDashboardTaskEmpty">Nothing needs your attention here.</div>}
            </div>
          </section>

          <section className="unionDashboardSection unionDashboardCalendarSection">
            <div className="unionDashboardSectionHeading isRow">
              <div><span>Calendar</span><h2>Tournament schedule</h2></div>
              <p>Select a busy day to open its tournaments.</p>
            </div>
            <div className="unionDashboardCalendar">
              <header>
                <button type="button" onClick={() => setCalendarMonth(month => month.clone().subtract(1, 'month'))} aria-label="Previous month">‹</button>
                <strong>{calendarMonth.format('MMMM YYYY')}</strong>
                <button type="button" onClick={() => setCalendarMonth(month => month.clone().add(1, 'month'))} aria-label="Next month">›</button>
              </header>
              <div className="unionDashboardCalendarWeekdays">{['Mon','Tue','Wed','Thu','Fri','Sat','Sun'].map(day => <span key={day}>{day}</span>)}</div>
              <div className="unionDashboardCalendarGrid">
                {calendarCells.map((day, index) => {
                  if (!day) return <span className="isBlank" key={`blank-${index}`} />;
                  const count = calendarCountByDay.get(day.format('YYYY-MM-DD')) || 0;
                  return (
                    <button
                      type="button"
                      key={day.format('YYYY-MM-DD')}
                      className={`${count ? 'hasTournaments' : ''}${day.isSame(moment(), 'day') ? ' isToday' : ''}`}
                      onClick={() => count && openCalendarDay(day)}
                      disabled={!count}
                    >
                      <b>{day.date()}</b>
                      {count ? <small>{count} {count === 1 ? 'tournament' : 'tournaments'}</small> : null}
                    </button>
                  );
                })}
              </div>
            </div>
          </section>

          <section className="unionDashboardSection">
            <div className="unionDashboardSectionHeading isRow">
              <div><span>Schedule</span><h2>Coming up next</h2></div>
              <Link to="/tournaments/upcoming">See all tournaments →</Link>
            </div>
            {data.tournaments.length ? (
              <div className="unionDashboardTournamentGrid">
                {data.tournaments.slice(0, 6).map(tournament => {
                  const capacity = getCapacity(tournament);
                  const spaces = Math.max(0, capacity.capacity - capacity.booked);
                  const picture = getPicture(tournament);
                  return (
                    <article key={tournament.id} className="unionDashboardTournament" onClick={() => openTournament(tournament.id)}>
                      <div className="unionDashboardTournamentImage" style={picture ? { backgroundImage: `url(${picture})` } : undefined}>
                        {!picture ? <span>{moment(tournament.startTime).format('DD')}</span> : null}
                        <em>{moment(tournament.startTime).format('ddd, DD MMM')}</em>
                      </div>
                      <div className="unionDashboardTournamentBody">
                        <small>{tournament.sport && tournament.sport.name ? tournament.sport.name : 'Tournament'}</small>
                        <h3>{tournament.name}</h3>
                        <p>{moment(tournament.startTime).format('HH:mm')} · {getVenue(tournament)}</p>
                        <div className="unionDashboardTournamentMetrics">
                          <span><b>{number(tournament.teamsCount) || capacity.booked}</b> teams</span>
                          <span><b>{capacity.capacity ? spaces : '—'}</b> spaces</span>
                          <span><b>{number(tournament.participantsCount)}</b> schools</span>
                        </div>
                        <div className="unionDashboardProgress">
                          <span><i style={{ width: `${propz.get(tournament, ['progress', 'percent'], 0)}%` }} /></span>
                          <small>
                            {propz.get(tournament, ['progress', 'totalEvents'], 0)
                              ? `${propz.get(tournament, ['progress', 'finishedEvents'], 0)} of ${propz.get(tournament, ['progress', 'totalEvents'], 0)} events complete`
                              : 'Events not created yet'}
                          </small>
                        </div>
                        <div className="unionDashboardTournamentFlags">
                          {tournament.hasUnansweredConfirmationRequests ? <span className="isWarning">Replies needed</span> : null}
                          {propz.get(tournament, ['publicSite', 'status']) !== 'DISABLED' ? <span>Public site live</span> : <span>Site off</span>}
                          {propz.get(tournament, ['publicBigscreenSite', 'status']) !== 'DISABLED' ? <span>TV live</span> : null}
                        </div>
                        <div className="unionDashboardTournamentActions">
                          <button type="button" onClick={event => openTournamentSection(event, tournament.id, '/tournaments/events')}>Results</button>
                          {number(tournament.waitingListSchoolsCount) ? <button type="button" onClick={event => openTournamentSection(event, tournament.id, '/tournaments/waitingList')}>Waiting list</button> : null}
                          {tournament.hasUnansweredConfirmationRequests ? <button type="button" onClick={event => openTournamentSection(event, tournament.id, '/tournaments/confirmationRequests')}>Replies</button> : null}
                          <button type="button" onClick={event => openTournamentSection(event, tournament.id, '/tournaments/manage')}>Manage</button>
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            ) : <div className="unionDashboardEmpty">No upcoming tournaments yet.</div>}
          </section>

          <section className="unionDashboardQuick">
            <h2>Quick actions</h2>
            <div>
              <Link to="/tournaments/upcoming">Manage tournaments</Link>
              <Link to="/championships">Open championships</Link>
              <Link to="/schools">Manage schools</Link>
              <Link to="/venues">Manage venues</Link>
              <Link to="/tournamentTags">Tournament tags</Link>
            </div>
          </section>
        </>
      ) : null}

      <SimpleModal
        isOpen={error}
        title="Unable to load dashboard"
        body="We couldn't load the latest union information. Please try again."
        buttonText="Try again"
        hideCancelButton
        onButtonClick={load}
        onCloseClick={() => setError(false)}
        dialogStyle={{ maxWidth: 520 }}
      />
    </main>
  );
};
